/**
 * Клиент API.
 *
 * Токены лежат в HttpOnly-cookie и в JS недоступны — браузер отправляет их сам.
 * Задача клиента: подставлять CSRF-заголовок и, получив 401, один раз сходить
 * на /auth/refresh и повторить запрос. Параллельные 401 ждут одно общее
 * обновление, иначе каждый запрос ротировал бы refresh-токен и попадал бы под
 * детекцию повторного использования.
 */

export const CSRF_COOKIE = 'ut_csrf'
export const CSRF_HEADER = 'X-CSRF-Token'

const BASE = '/api/v1'
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

export interface ApiErrorBody {
  code: string
  message: string
  requestId?: string
  details?: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  readonly requestId: string | undefined

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.requestId
  }

  /** Ошибка относится к конкретному полю формы? */
  get fieldErrors(): Array<{ field: string; message: string }> {
    if (this.code !== 'validation_error' || !Array.isArray(this.details)) return []
    return this.details as Array<{ field: string; message: string }>
  }
}

export function readCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

type Listener = () => void
const sessionLostListeners = new Set<Listener>()

/** Срабатывает, когда обновить сессию не удалось и нужен повторный вход. */
export function onSessionLost(listener: Listener): () => void {
  sessionLostListeners.add(listener)
  return () => sessionLostListeners.delete(listener)
}

let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      return res.ok
    } catch {
      return false
    } finally {
      // Слот освобождается сразу: все, кто уже ждал этот запрос, держат ссылку
      // на промис, а следующий 401 должен начать новый цикл обновления.
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Внутренний флаг: запрос уже повторялся после обновления токена. */
  retried?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfToken()
    if (csrf) headers[CSRF_HEADER] = csrf
  }

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    // fetch отклоняется, не дойдя до сервера: нет сети, запрос заблокирован
    // расширением, страница открыта не с того адреса. Отличаем это от ответа
    // сервера с ошибкой — иначе причина теряется.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, {
      code: 'network_unreachable',
      message: `Не удалось связаться с сервером (${location.origin}). `
        + 'Проверьте подключение к интернету.',
    })
  }

  if (res.status === 401 && !options.retried && path !== '/auth/refresh') {
    const refreshed = await refreshSession()
    if (refreshed) {
      return request<T>(path, { ...options, retried: true })
    }
    sessionLostListeners.forEach((listener) => listener())
  }

  if (!res.ok) {
    let body: ApiErrorBody = {
      code: 'bad_response',
      message: `Сервер вернул ответ ${res.status}, который не является ответом API.`,
    }
    try {
      const parsed = (await res.json()) as { error?: Record<string, unknown> }
      if (parsed.error) {
        body = {
          code: String(parsed.error.code ?? 'error'),
          message: String(parsed.error.message ?? 'Ошибка запроса'),
          ...(parsed.error.request_id ? { requestId: String(parsed.error.request_id) } : {}),
          ...(parsed.error.details !== undefined ? { details: parsed.error.details } : {}),
        }
      }
    } catch {
      // тело не JSON — оставляем сообщение по умолчанию
    }
    throw new ApiError(res.status, body)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
